/**
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema Golden Sisters
 * Arquivo: src/components/forms/AddRentalForm.js
 * Versão: 72.26 - RESTORE: Correção Pós-Conflito
 * Data: 11/12/2025
 * Responsável pela Alteração: Gemini
 */

import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../firebase/config';
import { Loader2, Search, X } from 'lucide-react';

import { createRental, getCustomers } from '../../services/firestoreService';
import productService from '../../services/productService';

const RENTAL_FEE_AVULSO = 29.90;

const PLAN_ELIGIBILITY_RULES = {
    'Bronze': { maxPieceValue: 150.00, allowedGroups: ['C'], maxPieces: null },
    'Prata': { maxPieceValue: 350.00, allowedGroups: ['C', 'B'], maxPieces: null },
    'Ouro': { maxPieceValue: 600.00, allowedGroups: ['C', 'B', 'A'], maxPieces: null },
    'Diamante': { maxPieceValue: Infinity, allowedGroups: ['C', 'B', 'A'], maxPieces: null },
    'Aspirante': { maxPieceValue: Infinity, allowedGroups: ['C'], maxPieces: 2, fixedFee: RENTAL_FEE_AVULSO }
};

const AddRentalForm = ({ onFormSubmit, onCancel }) => {
    const [searchTermCustomer, setSearchTermCustomer] = useState('');
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    
    const [rentalType, setRentalType] = useState('aspirant'); 
    const [planName, setPlanName] = useState('Aspirante'); 

    const [searchTermPiece, setSearchTermPiece] = useState('');
    const [filteredPieces, setFilteredPieces] = useState([]);
    const [selectedPieces, setSelectedPieces] = useState([]);
    const [allProductsMasterList, setAllProductsMasterList] = useState([]);
    const [pieceSearchLoading, setPieceSearchLoading] = useState(true);

    const [rentalStartDate, setRentalStartDate] = useState('');
    const [rentalEndDate, setRentalEndDate] = useState('');
    const [rentalFee, setRentalFee] = useState('');
    const [caucaoAmount, setCaucaoAmount] = useState('');
    const [caucaoPaymentMethod, setCaucaoPaymentMethod] = useState('credit_card');
    const [caucaoStatus, setCaucaoStatus] = useState('held');

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loggedInUser, setLoggedInUser] = useState(null);

    // Auth Observer
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            if (user) {
                setLoggedInUser({ uid: user.uid, name: user.displayName || user.email });
            } else {
                setLoggedInUser(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // Carregar produtos elegíveis
    const fetchAllClubPieces = useCallback(async () => {
        setPieceSearchLoading(true);
        setError('');
        try {
            const allProductsSnapshot = await productService.getAllProducts();
            const eligiblePieces = allProductsSnapshot.map(doc => ({ id: doc.id, ...doc.data() }))
                                                    .filter(piece =>
                                                        (piece.productType === 'clube' || piece.productType === 'venda_clube' || piece.rentalGroups?.length > 0) 
                                                        && piece.isActive === true && piece.stock > 0
                                                    );
            setAllProductsMasterList(eligiblePieces);
        } catch (err) {
            console.error("Erro ao carregar peças:", err);
            setError("Erro ao carregar lista de peças.");
        } finally {
            setPieceSearchLoading(false);
        }
    }, []);

    useEffect(() => {
        if (loggedInUser && allProductsMasterList.length === 0) {
            fetchAllClubPieces();
        }
    }, [loggedInUser, allProductsMasterList.length, fetchAllClubPieces]);

    // Determinar Plano
    useEffect(() => {
        if (selectedCustomer) {
            const activePlan = selectedCustomer.activeSubscription?.planName; 
            const rootSubscriptionPlan = selectedCustomer.subscriptionPlan; 

            let determinedPlanName = 'Aspirante'; 

            if (activePlan && activePlan !== 'Aspirante') {
                determinedPlanName = activePlan;
            } else if (rootSubscriptionPlan && rootSubscriptionPlan !== 'Aspirante') {
                determinedPlanName = rootSubscriptionPlan;
            } else if (selectedCustomer.planDetails?.id && selectedCustomer.planDetails.id !== 'Aspirante') { 
                determinedPlanName = selectedCustomer.planDetails.id;
            }
            
            let finalPlanNameKey = String(determinedPlanName || '').replace(/"/g, '').trim(); 
            if (finalPlanNameKey.startsWith('Clube ')) finalPlanNameKey = finalPlanNameKey.replace('Clube ', '');
            else if (finalPlanNameKey.startsWith('Plano ')) finalPlanNameKey = finalPlanNameKey.replace('Plano ', '');
            
            finalPlanNameKey = finalPlanNameKey.charAt(0).toUpperCase() + finalPlanNameKey.slice(1);

            setPlanName(finalPlanNameKey);
            
            if (finalPlanNameKey !== 'Aspirante') { 
                setRentalType('plan');
            } else {
                setRentalType('aspirant');
            }
            setError(''); 
        } else { 
            setRentalType('aspirant');
            setPlanName('Aspirante');
        }
    }, [selectedCustomer]); 

    // Busca de Clientes
    useEffect(() => {
        const fetchCustomers = async () => {
            if (searchTermCustomer.length < 3) {
                setFilteredCustomers([]);
                setCustomerSearchLoading(false);
                return;
            }

            setCustomerSearchLoading(true);
            setError('');
            try {
                const customers = await getCustomers();
                const lowerCaseSearchTerm = searchTermCustomer.toLowerCase();
                
                const filtered = customers.filter(customer => {
                    const nameMatch = customer.name?.toLowerCase().includes(lowerCaseSearchTerm);
                    const emailMatch = customer.email?.toLowerCase().includes(lowerCaseSearchTerm);
                    const phoneMatch = customer.phone?.toLowerCase().includes(lowerCaseSearchTerm); 
                    return nameMatch || emailMatch || phoneMatch;
                });
                setFilteredCustomers(filtered);
            } catch (err) {
                console.error("Erro buscar clientes:", err);
                setError("Erro ao buscar clientes.");
            } finally {
                setCustomerSearchLoading(false);
            }
        };

        const handler = setTimeout(() => { fetchCustomers(); }, 500);
        return () => { clearTimeout(handler); };
    }, [searchTermCustomer]);


    // Dependências Corrigidas
    useEffect(() => {
        if (rentalType === 'aspirant') {
            setRentalFee(RENTAL_FEE_AVULSO.toFixed(2));
            const totalCaucaoForAspirant = selectedPieces.reduce((sum, piece) => sum + (piece.price || 0), 0);
            setCaucaoAmount(selectedPieces.length > 0 ? totalCaucaoForAspirant.toFixed(2) : '');
            setCaucaoPaymentMethod('credit_card');
        } else {
            setCaucaoAmount('0'); 
            setCaucaoPaymentMethod('golden_points'); 
            setRentalFee('0.00');
        }
    }, [rentalType, selectedPieces, planName]); 

    // Filtro de Peças
    const filterPiecesByPlan = useCallback((term) => {
        let eligiblePieces = [...allProductsMasterList];
        const lowerCaseTerm = term.toLowerCase();
        
        if (lowerCaseTerm.length > 0) {
            eligiblePieces = eligiblePieces.filter(piece =>
                (piece.name && piece.name.toLowerCase().includes(lowerCaseTerm)) ||
                (piece.sku && piece.sku.toLowerCase().includes(lowerCaseTerm))
            );
        }

        const currentRules = rentalType === 'plan' ? PLAN_ELIGIBILITY_RULES[planName] : PLAN_ELIGIBILITY_RULES['Aspirante'];

        if (!currentRules) {
            setFilteredPieces([]);
            return;
        }

        eligiblePieces = eligiblePieces.filter(piece => {
            if (!Array.isArray(piece.rentalGroups) || piece.rentalGroups.length === 0) return false;
            const isGroupAllowed = piece.rentalGroups.some(group => currentRules.allowedGroups.includes(group));
            if (!isGroupAllowed) return false;
            if (piece.price > currentRules.maxPieceValue) return false;
            return true;
        });

        const finalFiltered = eligiblePieces.filter(piece => !selectedPieces.some(sp => sp.id === piece.id));
        setFilteredPieces(finalFiltered);
    }, [allProductsMasterList, selectedPieces, planName, rentalType]); 

    useEffect(() => {
        const handlerPiece = setTimeout(() => { filterPiecesByPlan(searchTermPiece); }, 300);
        return () => clearTimeout(handlerPiece);
    }, [searchTermPiece, planName, rentalType, filterPiecesByPlan]);


    const handleAddPiece = useCallback((piece) => {
        const currentRules = rentalType === 'plan' ? PLAN_ELIGIBILITY_RULES[planName] : PLAN_ELIGIBILITY_RULES['Aspirante'];

        if (!currentRules) {
            setError("Regras do plano não definidas.");
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (currentRules.maxPieces !== null && selectedPieces.length >= currentRules.maxPieces) {
            setError(`O limite de ${currentRules.maxPieces} peças foi atingido.`);
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (selectedPieces.some(p => p.id === piece.id)) {
            setError("Esta peça já foi adicionada.");
            setTimeout(() => setError(''), 3000);
            return;
        }

        if (piece.stock <= 0) {
            setError(`Estoque insuficiente: ${piece.name}.`);
            setTimeout(() => setError(''), 3000);
            return;
        }
        
        const isPieceEligible = currentRules.allowedGroups.some(group => piece.rentalGroups && piece.rentalGroups.includes(group)) &&
                                    piece.price <= currentRules.maxPieceValue;
        if (!isPieceEligible) {
            setError(`Peça não elegível para o plano ou excede o valor limite.`);
            setTimeout(() => setError(''), 5000);
            return;
        }

        setSelectedPieces(prev => [...prev, piece]);
        setSearchTermPiece('');
        setFilteredPieces([]);
    }, [planName, rentalType, selectedPieces]); 

    const handleRemovePiece = (pieceId) => {
        setSelectedPieces(prev => prev.filter(piece => piece.id !== pieceId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccess('');

        if (!selectedCustomer && !searchTermCustomer.trim()) { 
            setError("Selecione um cliente ou digite o nome para avulso.");
            setIsLoading(false); return;
        }

        if (rentalType === 'plan' && planName === 'Aspirante') { 
            setError("Cliente Aspirante. Use o modo Avulso.");
            setIsLoading(false); return;
        }
        
        if (rentalType === 'aspirant' && selectedCustomer) { 
            if (!searchTermCustomer.trim() && !selectedCustomer.name) {
                 setError("Confirme o nome do cliente.");
                 setIsLoading(false); return;
            }
        }

        if (selectedPieces.length === 0) {
            setError("Selecione pelo menos uma peça.");
            setIsLoading(false); return;
        }
        if (!rentalStartDate || !rentalEndDate) {
            setError("Selecione as datas.");
            setIsLoading(false); return;
        }
        if (new Date(rentalStartDate) >= new Date(rentalEndDate)) {
            setError("Data de início deve ser anterior ao fim.");
            setIsLoading(false); return;
        }

        if (!loggedInUser) {
            setError("Usuário não autenticado.");
            setIsLoading(false); return;
        }

        try {
            await createRental({
                customerId: selectedCustomer?.id || null, 
                customerName: selectedCustomer?.name || searchTermCustomer.trim(), 
                pieces: selectedPieces.map(piece => ({
                    id: piece.id,
                    name: piece.name,
                    sku: piece.sku,
                    price: piece.price,
                    rentalGroups: piece.rentalGroups
                })),
                rentalStartDate: new Date(rentalStartDate).toISOString(),
                rentalEndDate: new Date(rentalEndDate).toISOString(),
                rentalType, 
                planName: rentalType === 'plan' ? planName : null, 
                caucaoAmount: parseFloat(caucaoAmount),
                caucaoPaymentMethod,
                caucaoStatus,
                rentalFee: parseFloat(rentalFee),
                status: 'active',
                recordedBy: { uid: loggedInUser.uid, name: loggedInUser.name },
            });

            setSuccess('Aluguel registrado!');
            setSearchTermCustomer('');
            setFilteredCustomers([]);
            setSelectedCustomer(null);
            setRentalType('aspirant'); 
            setPlanName('Aspirante'); 
            setSelectedPieces([]);
            setSearchTermPiece('');
            setFilteredPieces([]);
            setRentalStartDate('');
            setRentalEndDate('');
            setCaucaoAmount('');
            setCaucaoPaymentMethod('credit_card');
            setCaucaoStatus('held');
            setRentalFee('');

            if (onFormSubmit) setTimeout(() => { onFormSubmit(); }, 1500);

        } catch (err) {
            console.error('Erro ao registrar:', err);
            setError(err.message || "Erro desconhecido.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-6 bg-brand-dark rounded-lg shadow-xl text-white">
            <h2 className="text-2xl font-bold mb-6 text-brand-gold">Registrar Novo Aluguel</h2>

            {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
            {success && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{success}</p>}

            {/* Busca Cliente */}
            <div className="mb-4">
                <label className="block text-gray-300 text-sm font-bold mb-2">Cliente</label>
                {selectedCustomer ? (
                    <div className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                        <span className="text-lg font-semibold">{selectedCustomer.name}</span>
                        <button type="button" onClick={() => { 
                            setSelectedCustomer(null); 
                            setSearchTermCustomer(''); 
                            setFilteredCustomers([]); 
                            setPlanName('Aspirante'); 
                            setRentalType('aspirant'); 
                        }} className="text-red-400 hover:text-red-600">
                            Remover Cliente
                        </button>
                    </div>
                ) : (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                        <input
                            type="text"
                            placeholder="Buscar cliente ou digitar avulso..."
                            value={searchTermCustomer}
                            onChange={(e) => setSearchTermCustomer(e.target.value)}
                            className="w-full p-3 pl-10 bg-gray-800 border border-gray-700 rounded-md focus:ring-brand-gold focus:border-brand-gold text-white"
                        />
                        {customerSearchLoading && <div className="absolute inset-y-0 right-0 flex items-center pr-3"><Loader2 className="h-5 w-5 animate-spin text-brand-gold" /></div>}
                        {searchTermCustomer.length >= 3 && filteredCustomers.length > 0 && (
                            <ul className="absolute z-10 w-full bg-gray-800 border border-gray-700 rounded-md mt-1 max-h-48 overflow-y-auto">
                                {filteredCustomers.map(customer => (
                                    <li
                                        key={customer.id}
                                        onClick={() => {
                                            setSelectedCustomer(customer);
                                            setFilteredCustomers([]);
                                            setSearchTermCustomer(customer.name); 
                                        }}
                                        className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0"
                                    >
                                        <p className="font-semibold">{customer.name}</p>
                                        <p className="text-sm text-gray-400">{customer.email || customer.phone}</p>
                                        {customer.activeSubscription?.planName && <p className="text-sm text-brand-gold">Plano: {customer.activeSubscription.planName}</p>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Tipo e Plano */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="block text-gray-300 text-sm font-bold mb-2">Tipo</label>
                    <input type="text" value={rentalType === 'plan' ? 'Plano Golden' : 'Avulso'} className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white font-semibold" disabled />
                </div>
                <div>
                    <label className="block text-gray-300 text-sm font-bold mb-2">Plano</label>
                    <input type="text" value={planName === 'Aspirante' ? 'N/A' : `Plano ${planName}`} className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white font-semibold" disabled />
                </div>
            </div>

            {/* Peças */}
            <div className="mb-4">
                <label className="block text-gray-300 text-sm font-bold mb-2">
                    Peça(s) {rentalType === 'aspirant' && `(Máx. ${PLAN_ELIGIBILITY_RULES.Aspirante.maxPieces})`}
                </label>
                {selectedPieces.length > 0 && (
                    <div className="space-y-2 mb-2">
                        {selectedPieces.map(piece => (
                            <div key={piece.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-md">
                                <span className="text-lg font-semibold">{piece.name}</span>
                                <button type="button" onClick={() => handleRemovePiece(piece.id)} className="text-red-400 hover:text-red-600"><X size={20} /></button>
                            </div>
                        ))}
                    </div>
                )}

                {!(rentalType === 'aspirant' && selectedPieces.length >= PLAN_ELIGIBILITY_RULES.Aspirante.maxPieces) && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                        <input
                            type="text"
                            placeholder="Adicionar peças..."
                            value={searchTermPiece}
                            onChange={(e) => setSearchTermPiece(e.target.value)}
                            className="w-full p-3 pl-10 pr-10 bg-gray-800 border border-gray-700 rounded-md focus:ring-brand-gold text-white"
                            disabled={rentalType === 'plan' && planName === 'Aspirante'}
                        />
                        {pieceSearchLoading && <div className="absolute inset-y-0 right-0 flex items-center pr-3"><Loader2 className="h-5 w-5 animate-spin text-brand-gold" /></div>}
                        {searchTermPiece.length >= 1 && filteredPieces.length > 0 && (
                            <ul className="absolute z-10 w-full bg-gray-800 border border-gray-700 rounded-md mt-1 max-h-48 overflow-y-auto">
                                {filteredPieces.map(piece => (
                                    <li
                                        key={piece.id}
                                        onClick={() => handleAddPiece(piece)}
                                        className="p-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700"
                                    >
                                        <p className="font-semibold">{piece.name}</p>
                                        <p className="text-sm text-gray-400">SKU: {piece.sku} | Estoque: {piece.stock}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Datas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="block text-gray-300 text-sm font-bold mb-2">Início</label>
                    <input type="date" value={rentalStartDate} onChange={(e) => setRentalStartDate(e.target.value)} className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white" required />
                </div>
                <div>
                    <label className="block text-gray-300 text-sm font-bold mb-2">Fim</label>
                    <input type="date" value={rentalEndDate} onChange={(e) => setRentalEndDate(e.target.value)} className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white" required />
                </div>
            </div>

            {/* Valores */}
            {rentalType === 'aspirant' && (
                <div className="mb-4">
                    <label className="block text-gray-300 text-sm font-bold mb-2">Aluguel</label>
                    <input type="text" value={`R$ ${rentalFee.replace('.',',')}`} className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white font-semibold" disabled />
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="block text-gray-300 text-sm font-bold mb-2">Caução {rentalType === 'plan' && '(Bloqueio Pontos)'}</label>
                    <input type="number" value={caucaoAmount} onChange={(e) => setCaucaoAmount(e.target.value)} className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white" step="0.01" required={rentalType === 'aspirant'} disabled={rentalType === 'plan' || (rentalType === 'aspirant' && selectedPieces.length > 0 && parseFloat(caucaoAmount) > 0)} />
                </div>
                <div>
                    <label className="block text-gray-300 text-sm font-bold mb-2">Pagamento Caução</label>
                    <select value={caucaoPaymentMethod} onChange={(e) => setCaucaoPaymentMethod(e.target.value)} className="w-full p-3 bg-gray-800 border border-gray-700 rounded-md text-white" disabled={rentalType === 'plan'}>
                        <option value="credit_card">Cartão de Crédito</option>
                        <option value="golden_points">Golden Points</option>
                        <option value="mixed">Ambos (Cartão/Pontos)</option>
                    </select>
                </div>
            </div>

            <div className="flex items-center justify-end space-x-4">
                <button type="button" onClick={onCancel} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg">Cancelar</button>
                <button type="submit" disabled={isLoading} className="bg-brand-gold hover:bg-yellow-600 text-brand-black font-bold py-2 px-6 rounded-lg flex items-center">
                    {isLoading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Registrando...</> : 'Registrar Aluguel'}
                </button>
            </div>
        </form>
    );
};

export default AddRentalForm;